import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, Check, Zap, Crown, Building2, Loader2, LogOut } from 'lucide-react';
import { billingApi } from '../services/api';
import api from '../services/api';
import { CPG_TIERS, isCpgProfile } from '../lib/planTiers';
import clsx from 'clsx';


const PRICING_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 200,
    description: 'Perfect for small cafes and food trucks',
    icon: Zap,
    features: [
      'POS on any tablet (iPad, Android)',
      'Kitchen display',
      'Basic inventory',
      'Staff management',
      'Email support',
    ],
  },
  {
    id: 'essentials',
    name: 'Essentials',
    price: 500,
    description: 'Great for growing restaurants',
    icon: Crown,
    popular: true,
    features: [
      'Kitchen display + station routing',
      'Full inventory with recipes',
      'Staff management',
      'Payroll & tip tracking',
      'Analytics dashboard',
      'Priority support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 1000,
    description: 'For multi-location operations',
    icon: Building2,
    features: [
      'Multi-location support',
      'Advanced inventory & waste tracking',
      'Full payroll with direct deposit',
      'Advanced analytics & forecasting',
      'API access',
      'Dedicated success manager',
    ],
  },
];

export default function SubscribePage() {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<string>('essentials');
  // CPG accounts see their one plan, not the restaurant tiers
  const [isCpg, setIsCpg] = useState(false);
  useEffect(() => {
    api.get('/v1/restaurants/current').then((r: any) => {
      if (isCpgProfile(r.data?.data?.settings)) { setIsCpg(true); setSelectedTier('cpg'); }
    }).catch(() => undefined);
  }, []);
  const tiers = isCpg ? CPG_TIERS : PRICING_TIERS;
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    if (!selectedTier) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await billingApi.createCheckout({
        tier: selectedTier,
        interval,
        successUrl: `${window.location.origin}/`,
        cancelUrl: `${window.location.origin}/subscribe`,
      });

      const url = response.data?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error?.message || 'Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('manager_token');
    localStorage.removeItem('manager_refresh_token');
    localStorage.removeItem('manager_user');
    navigate('/login');
  };

  const selectedPlan = ([...CPG_TIERS, ...PRICING_TIERS] as any[]).find(t => t.id === selectedTier);
  const yearlyDiscount = interval === 'yearly' ? 2 : 0; // 2 months free on yearly

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800">
      {/* Header */}
      <div className="p-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
            <ChefHat className="w-6 h-6 text-primary-600" />
          </div>
          <span className="text-xl font-bold text-white">Mise</span>
        </div>
        <button
          onClick={handleLogout}
          className="text-white/80 hover:text-white flex items-center gap-2 text-sm"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Choose your plan
          </h1>
          <p className="text-xl text-primary-100">
            Start running your restaurant smarter today
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-1 flex">
            <button
              onClick={() => setInterval('monthly')}
              className={clsx(
                'px-6 py-2 rounded-lg text-sm font-medium transition-all',
                interval === 'monthly'
                  ? 'bg-white text-primary-600'
                  : 'text-white hover:text-white/80'
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval('yearly')}
              className={clsx(
                'px-6 py-2 rounded-lg text-sm font-medium transition-all',
                interval === 'yearly'
                  ? 'bg-white text-primary-600'
                  : 'text-white hover:text-white/80'
              )}
            >
              Yearly
              <span className="ml-2 text-xs bg-success-500 text-white px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {tiers.map((tier: any) => {
            const isSelected = selectedTier === tier.id;
            const price = interval === 'yearly'
              ? Math.round(tier.price * 10) // 10 months for yearly
              : tier.price;
            const intro = (tier as any).intro;

            return (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className={clsx(
                  'relative bg-white rounded-2xl p-6 cursor-pointer transition-all',
                  isSelected
                    ? 'ring-4 ring-primary-400 shadow-2xl scale-105'
                    : 'hover:shadow-xl hover:scale-102',
                  tier.popular && 'md:-mt-4 md:mb-4'
                )}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <tier.icon className={clsx(
                    'w-10 h-10',
                    isSelected ? 'text-primary-600' : 'text-slate-400'
                  )} />
                  {isSelected && (
                    <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                <p className="text-slate-500 text-sm mt-1">{tier.description}</p>

                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold text-slate-900">${price}</span>
                  <span className="text-slate-500">/{interval === 'yearly' ? 'year' : 'month'}</span>
                  {interval === 'yearly' && (
                    <p className="text-sm text-success-600 mt-1">
                      2 months free
                    </p>
                  )}
                
                  {intro && <p className="mt-1 text-xs font-semibold text-emerald-700">{intro}</p>}</div>

                <ul className="space-y-3">
                  {tier.features.map((feature: string, i: number) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-success-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="max-w-md mx-auto mb-6 p-4 bg-danger-50 border border-danger-200 rounded-xl text-danger-700 text-sm text-center">
            {error}
          </div>
        )}

        {/* Subscribe Button */}
        <div className="text-center">
          <button
            onClick={handleSubscribe}
            disabled={isLoading || !selectedTier}
            className="btn bg-white text-primary-600 hover:bg-primary-50 px-12 py-4 text-lg font-semibold rounded-xl shadow-lg disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
                Processing...
              </>
            ) : (
              <>
                Subscribe to {selectedPlan?.name} - ${interval === 'yearly'
                  ? Math.round(selectedPlan!.price * 10)
                  : selectedPlan?.price}/{interval === 'yearly' ? 'year' : 'month'}
              </>
            )}
          </button>

          <p className="text-primary-200 text-sm mt-4">
            14-day free trial included. Cancel anytime.
          </p>
          <p className="text-primary-200 text-sm mt-2">
            Have a promo code? You can enter it at checkout.
          </p>
        </div>
      </div>
    </div>
  );
}
